// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { multiValues, tagPress } from '../../../bygning/fields';
import { shownChoice } from '../../../kortlaegning/cellDraft';
import { usePendingChoice } from '../../kortlaegning/usePendingChoice';
import styles from './Inspector.module.css';

export interface TagTogglesProps {
  label: string;
  options: readonly string[];
  /** The stored multiselect value (a JSON array of options). */
  value: string | null;
  disabled?: boolean;
  /** The new wire value (JSON array), or null once nothing is chosen. */
  onChange: (value: string | null) => void | Promise<void>;
}

/** A multiselect as toggle chips (v8 `tog()`): Genbrug · Genanvendelse · Nyttiggørelse. */
export function TagToggles({ label, options, value: stored, disabled, onChange }: TagTogglesProps) {
  // Shown, and judged against, while a toggle's write is in flight (b48394a).
  const { pending, choose } = usePendingChoice(onChange);
  const on = new Set(multiValues(shownChoice(stored, pending)));
  // A stored value the options no longer list still shows, so it can be turned off.
  const shown = [...options, ...[...on].filter((v) => !options.includes(v))];
  return (
    <div className={styles.tags} role="group" aria-label={label}>
      {shown.map((o) => (
        <button
          key={o}
          type="button"
          className={styles.tag}
          aria-pressed={on.has(o)}
          disabled={disabled}
          onClick={() => choose(tagPress(stored, pending, o, options))}
        >
          {o}
        </button>
      ))}
    </div>
  );
}
